import type { ReactNode } from 'react';

import { cn } from '../cn';
import type { Size } from './types';
import { sizeIconClass } from './types';
import Tooltip from './Tooltip';

type HoverTextPosition = 'top' | 'right';

export type ActionButtonColor = 'red' | 'green' | 'purple' | 'yellow' | 'orange' | 'gray';

const colorConfig: Record<ActionButtonColor, {
  gradient: string;
  shadowColor: string;
  hoverTextColor: string;
}> = {
  red: {
    gradient: 'bg-linear-to-br from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500',
    shadowColor: 'rgba(239, 68, 68, 0.7)',
    hoverTextColor: 'text-red-500',
  },
  green: {
    gradient: 'bg-linear-to-br from-emerald-700 to-emerald-800 hover:from-emerald-600 hover:to-emerald-700 active:from-emerald-800 active:to-emerald-900',
    shadowColor: 'rgba(6, 95, 70, 0.4)',
    hoverTextColor: 'text-emerald-700',
  },
  purple: {
    gradient: 'bg-linear-to-br from-violet-400 to-fuchsia-500 hover:from-violet-300 hover:to-fuchsia-400',
    shadowColor: 'rgba(167, 139, 250, 0.7)',
    hoverTextColor: 'text-violet-400',
  },
  yellow: {
    gradient: 'bg-linear-to-br from-yellow-500 to-orange-400 hover:from-yellow-400 hover:to-orange-300',
    shadowColor: 'rgba(234, 179, 8, 0.7)',
    hoverTextColor: 'text-yellow-500',
  },
  orange: {
    gradient: 'bg-linear-to-br from-orange-500 to-amber-400 hover:from-orange-400 hover:to-amber-300',
    shadowColor: 'rgba(251, 146, 60, 0.5)',
    hoverTextColor: 'text-orange-500',
  },
  gray: {
    gradient: 'bg-linear-to-br from-gray-400 to-gray-500 hover:from-gray-300 hover:to-gray-400',
    shadowColor: 'rgba(107, 114, 128, 0.5)',
    hoverTextColor: 'text-gray-400',
  },
};

export interface ActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  icon: ReactNode;
  size?: Size;
  color?: ActionButtonColor;
  /** Shadow color - defaults to the color's shadowColor if `color` is provided */
  shadowColor?: string;
  className?: string;
  hoverText?: string;
  hoverTextPosition?: HoverTextPosition;
  /** Hover text color - defaults to the color's hoverTextColor if `color` is provided */
  hoverTextColor?: string;
  progress?: number;
  /** Tooltip to show when button is disabled */
  disabledTooltip?: string;
  /** Popover target ID for popover API */
  popoverTarget?: string;
  /** Accessible name — icon-only buttons have no visible text, so pass one for a11y. */
  'aria-label'?: string;
}

// Square button sizes matching sizeHeight
function sizeSquare(size: Size = 'md') {
  return {
    '3xs': 'size-4',
    '2xs': 'size-5',
    xs: 'size-6',
    sm: 'size-8',
    md: 'size-9',
    lg: 'size-10',
    xl: 'size-12',
    '2xl': 'size-16',
    '3xl': 'size-20',
    '4xl': 'size-24',
  }[size];
}

// Progress arc configurations per size (SVG extends 8px beyond button on each side)
// SVG size = button size + 16px. Radius = (svgSize/2) - strokeWidth/2
function progressConfig(size: Size = 'md') {
  const configs: Record<Size, { viewBox: string; radius: number; circumference: number }> = {
    '3xs': { viewBox: '0 0 32 32', radius: 14, circumference: 88 },   // 16+16=32, r=14
    '2xs': { viewBox: '0 0 36 36', radius: 16, circumference: 100 },  // 20+16=36, r=16
    xs: { viewBox: '0 0 40 40', radius: 18, circumference: 113 },     // 24+16=40, r=18
    sm: { viewBox: '0 0 48 48', radius: 22, circumference: 138 },     // 32+16=48, r=22
    md: { viewBox: '0 0 52 52', radius: 24, circumference: 151 },     // 36+16=52, r=24
    lg: { viewBox: '0 0 56 56', radius: 26, circumference: 163 },     // 40+16=56, r=26
    xl: { viewBox: '0 0 64 64', radius: 30, circumference: 188 },     // 48+16=64, r=30
    '2xl': { viewBox: '0 0 80 80', radius: 38, circumference: 239 },  // 64+16=80, r=38
    '3xl': { viewBox: '0 0 96 96', radius: 46, circumference: 289 },  // 80+16=96, r=46
    '4xl': { viewBox: '0 0 112 112', radius: 54, circumference: 339 }, // 96+16=112, r=54
  };
  return configs[size];
}

export default function ActionButton({
  onClick,
  disabled,
  icon,
  size = 'lg',
  color,
  shadowColor: shadowColorProp,
  className,
  hoverText,
  hoverTextPosition = 'top',
  hoverTextColor: hoverTextColorProp,
  progress,
  disabledTooltip,
  popoverTarget,
  'aria-label': ariaLabel,
}: ActionButtonProps) {
  const buttonSize = sizeSquare(size);
  const iconSize = sizeIconClass(size);
  const progressCfg = progressConfig(size);
  const center = progressCfg.radius + 2; // center = radius + strokeWidth/2

  // Get color config if color is provided
  const config = color ? colorConfig[color] : undefined;
  const shadowColor = shadowColorProp ?? config?.shadowColor;
  const hoverTextColor = hoverTextColorProp ?? config?.hoverTextColor;

  const hoverTextClasses =
    hoverTextPosition === 'top'
      ? 'bottom-full mb-4 left-1/2 -translate-x-1/2 translate-y-1 group-hover:translate-y-0'
      : 'left-full ml-2 top-1/2 translate-y-0 -translate-x-1 group-hover:translate-x-0 group-hover:-translate-y-[calc(50%+2px)]';

  const content = (
    <div className={cn('group relative', disabled && 'pointer-events-auto')}>
      {/* Button wrapper that handles hover transforms - keeps ring and button aligned.
          active:scale-95 adds a tactile "press" on click; motion-reduce disables transforms. */}
      <div
        className={cn(
          'relative transition-all motion-reduce:transition-none',
          !disabled && 'group-hover:scale-110 group-hover:-translate-y-1 active:scale-95 motion-reduce:group-hover:scale-100 motion-reduce:group-hover:translate-y-0 motion-reduce:active:scale-100',
        )}>
        {progress !== undefined && shadowColor && (
          <svg
            className={cn(
              'absolute -inset-2 -rotate-90 pointer-events-none transition-opacity z-0',
              progress > 0 ? 'opacity-100' : 'opacity-0',
            )}
            viewBox={progressCfg.viewBox}>
            <circle
              cx={center}
              cy={center}
              r={progressCfg.radius}
              fill="none"
              stroke={shadowColor}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={`${progress * progressCfg.circumference} ${progressCfg.circumference}`}
              className="transition-[stroke-dasharray] duration-100"
            />
          </svg>
        )}
        <button
          onClick={onClick}
          disabled={disabled}
          aria-label={ariaLabel}
          popoverTarget={popoverTarget}
          className={cn(
            'relative flex items-center justify-center rounded-full text-white transition-all backdrop-blur-xs disabled:cursor-not-allowed overflow-hidden',
            'before:absolute before:inset-0 before:rounded-full before:bg-white/10 before:backdrop-blur-md',
            'after:absolute after:inset-0 after:rounded-full after:bg-linear-to-r after:from-transparent after:via-white/20 after:to-transparent after:translate-x-[-150%] group-hover:after:translate-x-[150%] after:transition-transform after:duration-700 after:ease-in-out',
            'shadow-[0_4px_16px_0_var(--shadow-color)] group-hover:shadow-[0_10px_24px_2px_var(--shadow-color)]',
            buttonSize,
            config?.gradient,
            className,
          )}
          style={{ '--shadow-color': shadowColor } as React.CSSProperties}>
          <span className={cn('relative z-10', iconSize, '[&>svg]:size-full')}>{icon}</span>
        </button>
      </div>
      {hoverText && !disabled && (
        <span
          className={cn(
            'absolute text-sm opacity-0 group-hover:opacity-100 transition-all whitespace-nowrap pointer-events-none',
            hoverTextClasses,
            hoverTextColor,
          )}>
          {hoverText}
        </span>
      )}
    </div>
  );

  if (disabled && disabledTooltip) {
    return <Tooltip tooltip={disabledTooltip}>{content}</Tooltip>;
  }

  return content;
}
